import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { usePathParam, useSecondOrderCallback } from '@chilecompra/react-kit'
import {
  Breadcrumb,
  Breadcrumbs,
  Button,
  Popover,
  PopoverMenuItem,
  Spinner,
  Switch,
  useSwitch,
  useViewport
} from '@chilecompra/react-kit/components'
import { DEFAULT_REGION, CURRENCY_VALUES } from '../../config/settings/constants'
import routes from '../../config/settings/routes'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { REGIONS_DICTIONARY, REGIONS_NUMBERS_DICTIONARY } from './RegionalSection.utils'
import { onGetRegionsThunk, onGetRegionalKPIsThunk } from './RegionalSection.actions'
import RegionalDataDialog from '../../components/Dialogs/RegionalDataDialog'
import {
  Action,
  ActionLink,
  AnchorRegion,
  AnchorRegionIcon,
  AnchorRegionLabel,
  AnchorRegionText,
  AnchorYear,
  AnchorYearIcon,
  AnchorYearLabel,
  AnchorYearText,
  BannerContainer,
  BannerImage,
  BannerImageContainer,
  BannerInfo,
  BannerInfoContainer,
  BannerText,
  Datum,
  ErrorMessage,
  GridContainer,
  GridItem,
  HelpIcon,
  Label,
  Root,
  Strong,
  Title,
  Value
} from './RegionalSection.styles'

/**
 * The RegionalSection's container.
 */
const RegionalSection = props => {
  const { children } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const regionSlug = usePathParam('regionSlug') || DEFAULT_REGION
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const {
    amount,
    amountUSD,
    biddings,
    error,
    loadingKPIs,
    loadingRegions,
    orders,
    regions: regionsOptions
  } = useSelector(state => state.regionalSection)
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const [helpOpen, setHelpOpen] = useState(false)
  const [regionsOpen, setRegionsOpen] = useState(false)
  const [regionValue, setRegionValue] = useState(regionSlug)
  const [yearsOpen, setYearsOpen] = useState(false)
  const [yearValue, setYearValue] = useState(endYear)
  const { size } = useViewport()
  const createHandleRegionChange = useSecondOrderCallback((_, updatedValue) => {
    navigate(routes.regionalInfo.replace(/:regionSlug/, updatedValue))
    setRegionsOpen(false)
  })
  const createHandleYearChange = useSecondOrderCallback(
    (_, year) => {
      setYearValue(year)
      setYearsOpen(false)
      dispatch(onGetRegionalKPIsThunk({ year, regionId: REGIONS_NUMBERS_DICTIONARY[regionValue] }))
    },
    [regionValue]
  )
  const selectedRegion = useMemo(
    () => regionsOptions.find(regionOption => regionOption.value === regionValue) || {},
    [regionsOptions, regionValue]
  )
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [])
  const handleCloseHelp = useCallback(() => setHelpOpen(false), [])
  const handleOpenHelp = useCallback(() => setHelpOpen(true), [])
  const handleCloseRegions = useCallback(() => setRegionsOpen(false), [])
  const handleOpenRegions = useCallback(() => setRegionsOpen(true), [])
  const handleCloseYears = useCallback(() => setYearsOpen(false), [])
  const handleOpenYears = useCallback(() => setYearsOpen(true), [])
  const handleRedirectToHome = useCallback(() => navigate(routes.root, { replace: true }))

  useEffect(() => {
    setRegionValue(regionSlug)
  }, [regionSlug])

  useEffect(() => {
    if (initialLoading) {
      return
    }

    setYearValue(endYear)
    batch(() => {
      dispatch(onGetRegionsThunk())
      dispatch(onGetRegionalKPIsThunk({ regionId: REGIONS_NUMBERS_DICTIONARY[regionValue], year: endYear }))
    })
  }, [endYear, initialLoading, regionValue])

  if (initialLoading || loadingKPIs || loadingRegions) {
    return (
      <GridContainer alignItems="center" height="100%">
        <GridItem align="center" xs={12}>
          <Spinner />
        </GridItem>
      </GridContainer>
    )
  }

  if (!!error && !initialLoading && !loadingKPIs && !loadingRegions) {
    return (
      <GridContainer alignItems="center" height="100%">
        <GridItem align="center" xs={12}>
          <ErrorMessage>
            No existe información asociada a la región “<Strong>{regionSlug}</Strong>”
          </ErrorMessage>
          <Button color="primary" onClick={handleRedirectToHome}>
            Volver al Inicio
          </Button>
        </GridItem>
      </GridContainer>
    )
  }

  return (
    <>
      <Root maxWidth={false}>
        <BannerContainer>
          <BannerInfoContainer>
            <BannerInfo>
              <GridContainer padding="0 0 32px">
                <GridItem xs={12}>
                  <Breadcrumbs>
                    <Breadcrumb to={routes.root} variant="link">
                      Datos Abiertos
                    </Breadcrumb>
                    <Breadcrumb>Información Regional</Breadcrumb>
                  </Breadcrumbs>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 32px">
                <GridItem xs={12}>
                  <Title>
                    <span>
                      Conoce la información de las <Strong>compras públicas</Strong> por cada región
                      <br /> de Chile
                    </span>
                  </Title>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 43px">
                <GridItem xs={12}>
                  <AnchorRegionLabel>selecciona una región</AnchorRegionLabel>
                  <Popover
                    anchor={
                      <AnchorRegion>
                        <AnchorRegionText>{selectedRegion?.name}</AnchorRegionText>
                        <AnchorRegionIcon open={regionsOpen} />
                      </AnchorRegion>
                    }
                    open={regionsOpen}
                    onClose={handleCloseRegions}
                    onOpen={handleOpenRegions}
                  >
                    {regionsOptions.map(regionOption => (
                      <PopoverMenuItem
                        enableClose
                        key={regionOption.value}
                        onClick={createHandleRegionChange(regionOption.value)}
                      >
                        {regionOption.name}
                      </PopoverMenuItem>
                    ))}
                  </Popover>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 16px">
                <GridItem align={size.isDownToSmall ? 'center' : 'left'} xs={12}>
                  <AnchorYearLabel>Cifras acumuladas en la región durante el año:&nbsp;</AnchorYearLabel>
                  <Popover
                    anchor={
                      <AnchorYear>
                        <AnchorYearText>{yearValue}</AnchorYearText>
                        <AnchorYearIcon open={yearsOpen} />
                      </AnchorYear>
                    }
                    open={yearsOpen}
                    onClose={handleCloseYears}
                    onOpen={handleOpenYears}
                  >
                    {yearsOptions.map(yearOption => (
                      <PopoverMenuItem
                        enableClose
                        key={yearOption.value}
                        onClick={createHandleYearChange(yearOption.value)}
                      >
                        {yearOption.name}
                      </PopoverMenuItem>
                    ))}
                  </Popover>
                </GridItem>
              </GridContainer>
              <GridContainer margin="16px 0 0" padding="0 0 16px 0">
                <GridItem align={size.isDownToSmall ? 'center' : 'left'} height="60px" xs={12}>
                  <Datum>
                    <Value>
                      {!currencyValue && `${CURRENCY_VALUES.CLP} ${formatAmount(parseInt(amount, 10))}`}
                      {currencyValue && `${CURRENCY_VALUES.USD} ${formatAmount(parseInt(amountUSD, 10))}`}
                    </Value>
                    <Label>Monto total transado</Label>
                  </Datum>
                </GridItem>
                <GridItem align={size.isDownToSmall ? 'center' : 'left'} height="60px" md={4} xs={12}>
                  <Datum>
                    <Value>{formatAmount(parseInt(orders, 10))}</Value>
                    <Label>Órdenes de compra</Label>
                  </Datum>
                </GridItem>
                <GridItem align={size.isDownToSmall ? 'center' : 'left'} height="60px" md={4} xs={12}>
                  <Datum>
                    <Value>{formatAmount(parseInt(biddings, 10))}</Value>
                    <Label>Licitaciones adjudicadas</Label>
                  </Datum>
                </GridItem>
              </GridContainer>
              <GridContainer direction={size.isDownToTiny ? 'column-reverse' : 'row'} padding="0 32px 32px 0">
                <GridItem align={size.isDownToSmall ? 'center' : 'left'} md={9} sm={8} xs={12}>
                  <Action>
                    <ActionLink onClick={handleOpenHelp}>
                      <HelpIcon>?</HelpIcon>
                      En qué se basa la información de esta región
                    </ActionLink>
                  </Action>
                </GridItem>
                <GridItem align={size.isDownToSmall ? 'center' : 'left'} md={3} sm={4} xs={12}>
                  <Switch
                    checked={currencyValue}
                    color="primary"
                    labelLeft="CLP"
                    labelRight="USD"
                    onChange={handleChangeCurrency}
                  />
                </GridItem>
              </GridContainer>
            </BannerInfo>
          </BannerInfoContainer>
          <BannerImageContainer>
            <BannerImage src={REGIONS_DICTIONARY[REGIONS_NUMBERS_DICTIONARY[regionValue]]?.source || ''} />
            <BannerText>{REGIONS_DICTIONARY[REGIONS_NUMBERS_DICTIONARY[regionValue]]?.slogan || ''}</BannerText>
          </BannerImageContainer>
        </BannerContainer>
      </Root>
      {children}
      <RegionalDataDialog open={helpOpen} onClose={handleCloseHelp} />
    </>
  )
}

RegionalSection.propTypes = {
  children: PropTypes.node.isRequired
}

export default RegionalSection
